// This file contains utility functions to interact with AWS Amplify's GraphQL API.
// It provides functions to call GraphQL queries and handle pagination, as well as batch processing of multiple queries.

import { API } from "aws-amplify";

export interface GraphQLParams {
  query: any;
  variables?: Record<string, any>;
  authMode?:
    | "AMAZON_COGNITO_USER_POOLS"
    | "API_KEY"
    | "AWS_IAM"
    | "OPENID_CONNECT";
  outputKeyword: string; // Optional keyword to extract specific data from the response
}

export async function callGraphQL<T = any>({
  query,
  variables = {},
  authMode = "AMAZON_COGNITO_USER_POOLS",
  outputKeyword,
}: GraphQLParams): Promise<T[]> {
  let allItems: T[] = [];
  let nextToken: string | null = null;

  do {
    const response: any = await API.graphql({
      query,
      variables: { ...variables, nextToken, limit: 99999 },
      authMode,
    });
    const data = response.data?.[outputKeyword];
    if (data?.items) {
      allItems = [...allItems, ...data.items];
    }
    nextToken = data?.nextToken ?? null;
  } while (nextToken);

  return allItems;
}
// Batch version: calls multiple queries in sequence or parallel
export async function callGraphQLBatch(
  requests: GraphQLParams[],
  inParallel = false
): Promise<any[]> {
  if (inParallel) {
    return Promise.all(requests.map((req) => callGraphQL(req)));
  } else {
    const results = [];
    for (const req of requests) {
      results.push(await callGraphQL(req));
    }
    return results;
  }
}

// ...existing code...

export interface GraphQLGetParams {
  query: any;
  variables?: Record<string, any>;
  authMode?:
    | "AMAZON_COGNITO_USER_POOLS"
    | "API_KEY"
    | "AWS_IAM"
    | "OPENID_CONNECT";
  outputKeyword: string; // The key to extract from response.data
}

/**
 * Calls a single GraphQL GET query and returns the object.
 */
export async function callGraphQLGet<T = any>({
  query,
  variables = {},
  authMode = "AMAZON_COGNITO_USER_POOLS",
  outputKeyword,
}: GraphQLGetParams): Promise<T | null> {
  const response: any = await API.graphql({
    query,
    variables,
    authMode,
  });
  return response.data?.[outputKeyword] ?? null;
}
